import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, Info, ExternalLink } from 'lucide-react';

interface AuditFormProps {
  onStartAudit: (url: string) => void;
  isLoading: boolean;
}

const AuditForm: React.FC<AuditFormProps> = ({ onStartAudit, isLoading }) => {
  const [websiteUrl, setWebsiteUrl] = useState('');

  const normalizeUrl = (url: string) => {
    const trimmedUrl = url.trim();
    // If URL doesn't start with http:// or https://, add https://
    if (!trimmedUrl.match(/^https?:\/\//i)) {
      return `https://${trimmedUrl}`;
    }
    return trimmedUrl;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (websiteUrl.trim()) {
      const normalizedUrl = normalizeUrl(websiteUrl);
      onStartAudit(normalizedUrl);
    }
  };

  const isValidUrl = (url: string) => {
    try {
      // Try to create URL with the normalized version
      const normalizedUrl = normalizeUrl(url);
      new URL(normalizedUrl);
      return true;
    } catch {
      return false;
    }
  };

  // Mock data for demonstration
  const mockIssues = [
    { id: 1, title: 'Missing Meta Description', severity: 'critical', status: 'pending' },
    { id: 2, title: 'Slow Page Load Time', severity: 'high', status: 'pending' },
    { id: 3, title: 'Missing Alt Text', severity: 'medium', status: 'pending' },
    { id: 4, title: 'Broken Internal Link', severity: 'low', status: 'pending' },
    { id: 5, title: 'Unoptimized Images', severity: 'medium', status: 'pending' },
    { id: 6, title: 'Missing H1 Tag', severity: 'critical', status: 'pending' },
    { id: 7, title: 'Duplicate Content', severity: 'high', status: 'pending' },
    { id: 8, title: 'Mobile Responsiveness', severity: 'medium', status: 'pending' },
    { id: 9, title: 'SSL Certificate Issues', severity: 'critical', status: 'pending' },
    { id: 10, title: 'Missing Schema Markup', severity: 'low', status: 'pending' }
  ];

  const criticalIssues = mockIssues.filter(issue => issue.severity === 'critical').length;
  const warningIssues = mockIssues.filter(issue => issue.severity === 'high' || issue.severity === 'medium').length;
  const totalIssues = mockIssues.length;

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'high':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'medium':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'low':
        return <Info className="h-4 w-4 text-blue-500" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-6">
        {/* Main Form */}
        <div className="flex-1">
          <Card className="p-8">
            <CardContent className="p-0">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <Input
                    type="text"
                    placeholder="Enter your website URL (e.g., example.com or https://example.com)..."
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    className="h-16 text-lg border-2 border-secondary focus:border-primary focus:ring-primary"
                    required
                  />
                  {websiteUrl && !isValidUrl(websiteUrl) && (
                    <p className="text-sm text-red-500">Please enter a valid website URL (e.g., example.com)</p>
                  )}
                </div>
                <div className="flex flex-col items-center space-y-2">
                  <Button 
                    type="submit" 
                    className="bg-white/90 text-primary-foreground shadow-xs hover:bg-white rounded-md h-12 px-8 text-lg"
                    disabled={isLoading || !websiteUrl || !isValidUrl(websiteUrl)}
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Starting Audit...
                      </>
                    ) : (
                      'Start AI Audit'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Status Box - 1/5th width */}
        <div className="w-1/5">
          <Card className="p-6">
            <CardContent className="p-0">
              <div className="space-y-4">
                {/* Error Count */}
                <div className="text-center">
                  <div className="text-4xl font-bold text-red-500">{totalIssues}</div>
                  <div className="text-sm text-muted-foreground">Issues Found</div>
                </div>

                {/* Progress Bars */}
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-red-600">Critical</span>
                      <span className="text-red-600">{criticalIssues}/{totalIssues}</span>
                    </div>
                    <Progress 
                      value={(criticalIssues / totalIssues) * 100} 
                      className="h-2"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-orange-600">Warnings</span>
                      <span className="text-orange-600">{warningIssues}/{totalIssues}</span>
                    </div>
                    <Progress 
                      value={(warningIssues / totalIssues) * 100} 
                      className="h-2"
                    />
                  </div>
                </div>

                {/* Fix with AI Button */}
                <Button 
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={totalIssues === 0}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Fix with AI
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Issues Preview Widget */}
      <div className="mt-6">
        <Card className="p-6">
          <CardContent className="p-0">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Recent Issues</h3>
                <Button variant="outline" size="sm">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View More
                </Button>
              </div>
              
              <div className="space-y-2">
                {mockIssues.slice(0, 10).map((issue) => (
                  <div key={issue.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {getSeverityIcon(issue.severity)}
                      <span className="text-sm font-medium">{issue.title}</span>
                    </div>
                    <Badge className={getSeverityColor(issue.severity)}>
                      {issue.severity}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuditForm;
